import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "../../components/Atomos/Icons/AddIcon";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import ConditionBuilder from "./ConditionBuilder";
import {
  Operator,
  RecipientCondition,
  RecipientConditionGroup,
  RecipientSelection,
  normalizeGroupConnectors,
} from "./types";
import { areRecipientSelectionsEqual } from "./recipientSelectionUtils";

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
  >
    <path
      d="M9 5L16 12L9 19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface RecipientListCardProps {
  onProceedToNextStep: (data: RecipientSelection) => void;
  excludedEmails?: string[];
  onRemoveExclusion?: (email: string) => void;
  hasTemplateSelected: boolean;
  prefilledOperator?: Operator | null;
  prefilledGroupConnectors?: Operator[];
  prefilledGroups?: RecipientConditionGroup[];
  prefillKey?: string;
  onPreviewRecipientList?: (data: RecipientSelection) => void;
  lastPreviewedSelection?: RecipientSelection | null;
  lastPreviewedExcludedEmails?: string[];
  isPreviewLoading?: boolean;
  previewCount?: number;
  isProceedLoading?: boolean;
  isPrefillLoading?: boolean;
}

const createEmptyCondition = (): RecipientCondition => ({
  id: "1",
  fieldId: "",
  conditionOperator: "is",
  valueId: "",
});

const createEmptyGroup = (
  id: string,
  logic: Operator = "AND",
): RecipientConditionGroup => ({
  id,
  logic,
  conditions: [createEmptyCondition()],
});

const cloneGroups = (
  groups: RecipientConditionGroup[],
): RecipientConditionGroup[] =>
  groups.map((group) => ({
    ...group,
    conditions: group.conditions.map((condition) => ({ ...condition })),
  }));

const getMaxGroupCounter = (groups: RecipientConditionGroup[]): number =>
  groups.reduce((maxValue, group) => {
    const match = group.id.match(/group-(\d+)$/);
    if (!match) {
      return maxValue;
    }
    const numericValue = Number(match[1]);
    return Number.isFinite(numericValue)
      ? Math.max(maxValue, numericValue)
      : maxValue;
  }, 0);

const hasValidCondition = (condition: RecipientCondition): boolean =>
  !!condition.fieldId?.trim() && !!condition.valueId?.trim();

const areConditionsEqual = (
  left: RecipientCondition[],
  right: RecipientCondition[],
): boolean => {
  if (left === right) {
    return true;
  }
  if (left.length !== right.length) {
    return false;
  }

  return left.every((condition, index) => {
    const otherCondition = right[index];
    if (!otherCondition) {
      return false;
    }

    return (
      condition.id === otherCondition.id &&
      condition.fieldId === otherCondition.fieldId &&
      condition.conditionOperator === otherCondition.conditionOperator &&
      condition.valueId === otherCondition.valueId
    );
  });
};

const normalizeStringArray = (values: string[]): string[] =>
  values
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  const normalizedLeft = normalizeStringArray(left);
  const normalizedRight = normalizeStringArray(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const getOperatorButtonClass = (isActive: boolean): string =>
  `w-[150px] h-[42px] border font-decimal font-medium text-[18px] leading-[20px] text-center transition-colors ${
    isActive
      ? "bg-Default text-white border-Default hover:bg-accent-Default-dark"
      : "bg-white text-Default border-gray-300 hover:border-Default hover:text-Default"
  }`;

const GROUP_OPERATOR_OPTIONS: Operator[] = ["AND", "OR"];

const GroupJoinDropdown: React.FC<{
  value: Operator;
  onChange: (value: Operator) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-font uppercase hover:text-Default transition-colors"
        aria-label="Combine groups with"
      >
        <span className="tracking-[0.24em]">{value}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[100px]">
          {GROUP_OPERATOR_OPTIONS.map((operatorOption) => (
            <button
              key={operatorOption}
              type="button"
              onClick={() => {
                onChange(operatorOption);
                setIsOpen(false);
              }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                value === operatorOption
                  ? "bg-gray-50 font-medium text-Default"
                  : "text-gray-font"
              }`}
            >
              {operatorOption}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function RecipientListCard({
  onProceedToNextStep,
  excludedEmails = [],
  onRemoveExclusion,
  hasTemplateSelected,
  prefilledOperator = null,
  prefilledGroupConnectors = [],
  prefilledGroups = [],
  prefillKey,
  onPreviewRecipientList,
  lastPreviewedSelection = null,
  lastPreviewedExcludedEmails = [],
  isPreviewLoading = false,
  previewCount,
  isProceedLoading = false,
  isPrefillLoading = false,
}: RecipientListCardProps): React.ReactElement {
  const groupIdCounter = useRef(1);
  const defaultJoinOperator = prefilledOperator || "AND";
  const [currentGroups, setCurrentGroups] = useState<RecipientConditionGroup[]>(
    () => {
      if (prefilledGroups.length > 0) {
        return cloneGroups(prefilledGroups);
      }
      return [createEmptyGroup("group-1")];
    },
  );
  const [groupConnectors, setGroupConnectors] = useState<Operator[]>(() =>
    normalizeGroupConnectors(
      prefilledGroups.length > 0
        ? prefilledGroups
        : [createEmptyGroup("group-1")],
      defaultJoinOperator,
      prefilledGroupConnectors,
    ),
  );

  useEffect(() => {
    if (isPrefillLoading) {
      return;
    }

    if (prefilledGroups.length > 0) {
      setCurrentGroups(cloneGroups(prefilledGroups));
      setGroupConnectors(
        normalizeGroupConnectors(
          prefilledGroups,
          prefilledOperator || "AND",
          prefilledGroupConnectors,
        ),
      );
      groupIdCounter.current = getMaxGroupCounter(prefilledGroups) + 1;
      return;
    }

    setCurrentGroups([createEmptyGroup("group-1")]);
    setGroupConnectors([]);
    groupIdCounter.current = 2;
  }, [
    prefilledGroups,
    prefilledGroupConnectors,
    prefilledOperator,
    prefillKey,
    isPrefillLoading,
  ]);

  const currentSelection = useMemo<RecipientSelection>(
    () => ({
      logic: groupConnectors.includes("OR") ? "OR" : defaultJoinOperator,
      groups: currentGroups,
      groupConnectors: normalizeGroupConnectors(
        currentGroups,
        defaultJoinOperator,
        groupConnectors,
      ),
    }),
    [currentGroups, defaultJoinOperator, groupConnectors],
  );

  const hasValidConditions = useMemo(
    () =>
      currentGroups.some((group) =>
        group.conditions.some((condition) => hasValidCondition(condition)),
      ),
    [currentGroups],
  );

  const hasUpToDatePreview = useMemo(() => {
    if (!hasValidConditions || !lastPreviewedSelection) {
      return false;
    }

    return (
      areRecipientSelectionsEqual(
        currentSelection,
        lastPreviewedSelection,
      ) &&
      areStringArraysEqual(excludedEmails, lastPreviewedExcludedEmails)
    );
  }, [
    currentSelection,
    excludedEmails,
    hasValidConditions,
    lastPreviewedExcludedEmails,
    lastPreviewedSelection,
  ]);

  const handlePreviewClick = useCallback(() => {
    if (!hasValidConditions || !onPreviewRecipientList) {
      return;
    }
    onPreviewRecipientList(currentSelection);
  }, [currentSelection, hasValidConditions, onPreviewRecipientList]);

  const canPreviewRecipientList =
    hasTemplateSelected &&
    hasValidConditions &&
    !!onPreviewRecipientList &&
    !isPreviewLoading;

  const handleAddGroup = useCallback(() => {
    setCurrentGroups((prev) => [
      ...prev,
      createEmptyGroup(`group-${groupIdCounter.current++}`),
    ]);
    setGroupConnectors((prev) => [...prev, "AND"]);
  }, []);

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      setCurrentGroups((prevGroups) => {
        const removeIndex = prevGroups.findIndex((group) => group.id === groupId);
        if (removeIndex === -1 || prevGroups.length <= 1) {
          return prevGroups;
        }

        setGroupConnectors((prevConnectors) => {
          if (!prevConnectors.length) {
            return prevConnectors;
          }
          const connectorIndex = removeIndex === 0 ? 0 : removeIndex - 1;
          return prevConnectors.filter((_, index) => index !== connectorIndex);
        });

        return prevGroups.filter((group) => group.id !== groupId);
      });
    },
    [],
  );

  const handleGroupConnectorChange = useCallback(
    (connectorIndex: number, logic: Operator) => {
      setGroupConnectors((prev) => {
        if (prev[connectorIndex] === logic) {
          return prev;
        }

        const next = [...prev];
        next[connectorIndex] = logic;
        return next;
      });
    },
    [],
  );

  const handleGroupLogicChange = useCallback(
    (groupId: string, logic: Operator) => {
      setCurrentGroups((prev) => {
        let hasChanges = false;
        const next = prev.map((group) => {
          if (group.id !== groupId) {
            return group;
          }
          if (group.logic === logic) {
            return group;
          }

          hasChanges = true;
          return { ...group, logic };
        });

        return hasChanges ? next : prev;
      });
    },
    [],
  );

  const handleGroupConditionsChange = useCallback(
    (groupId: string, conditions: RecipientCondition[]) => {
      setCurrentGroups((prev) => {
        let hasChanges = false;
        const next = prev.map((group) => {
          if (group.id !== groupId) {
            return group;
          }
          if (areConditionsEqual(group.conditions, conditions)) {
            return group;
          }

          hasChanges = true;
          return { ...group, conditions };
        });

        return hasChanges ? next : prev;
      });
    },
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 lg:px-8 py-5 border-b border-gray-100">
          <h3 className="text-[2rem] font-decimal font-medium leading-none text-Default">
            Step 2: Create Recipient List
          </h3>
        </div>

        <div className="px-6 lg:px-8 py-6 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            {isPrefillLoading ? (
              <p className="text-sm text-gray-500">
                Loading recipient list filters...
              </p>
            ) : (
              <>
                {currentGroups.map((group, index) => (
              <div key={group.id} className="flex flex-col gap-4">
                <div className="border border-gray-200 rounded-lg bg-[#FCFCFC] p-5 flex flex-col gap-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold tracking-[0.24em] text-gray-font uppercase">
                        Group {index + 1}
                      </span>
                      <span className="text-sm text-gray-font">
                        A recipient must match{" "}
                        <span className="font-medium text-Default">
                          {group.logic === "AND" ? "all" : "any"}
                        </span>{" "}
                        of the conditions in this group.
                      </span>
                    </div>

                    {currentGroups.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveGroup(group.id)}
                        className="text-sm font-medium text-feedback-error hover:text-primary transition-colors"
                      >
                        Remove group
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleGroupLogicChange(group.id, "AND")}
                        className={getOperatorButtonClass(group.logic === "AND")}
                        style={{
                          padding: "0 5px",
                          WebkitFontSmoothing: "antialiased",
                        }}
                      >
                        AND
                      </button>
                      <button
                        type="button"
                        onClick={() => handleGroupLogicChange(group.id, "OR")}
                        className={getOperatorButtonClass(group.logic === "OR")}
                        style={{
                          padding: "0 5px",
                          WebkitFontSmoothing: "antialiased",
                        }}
                      >
                        OR
                      </button>
                    </div>
                  </div>

                  <ConditionBuilder
                    operator={group.logic}
                    initialConditions={group.conditions}
                    resetKey={`${prefillKey || "manual"}-${group.id}`}
                    onConditionsChange={(conditions) =>
                      handleGroupConditionsChange(group.id, conditions)
                    }
                    canRemoveLastCondition={false}
                  />
                </div>

                {index < currentGroups.length - 1 && (
                  <div className="flex items-center gap-3">
                    <GroupJoinDropdown
                      value={groupConnectors[index] ?? "AND"}
                      onChange={(logic) =>
                        handleGroupConnectorChange(index, logic)
                      }
                    />
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                )}
              </div>
            ))}

            <div>
              <button
                type="button"
                onClick={handleAddGroup}
                className="inline-flex items-center justify-center gap-3 min-h-[42px] px-5 bg-white font-decimal font-medium text-[18px] leading-[20px] text-gray-font whitespace-nowrap hover:bg-gray-50 transition-colors"
                style={{
                  border: "1px solid #474F56",
                  WebkitFontSmoothing: "antialiased",
                }}
              >
                ADD GROUP
                <AddIcon />
              </button>
            </div>
              </>
            )}
          </div>

          {excludedEmails.length > 0 && (
            <div className="border border-gray-200 rounded-lg px-4 py-4 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-Default">
                  Global exclusions
                </span>
                <span className="text-sm text-gray-font">
                  These email addresses are removed from the final result,
                  regardless of which group matches.
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {excludedEmails.map((email) => (
                  <button
                    key={email}
                    type="button"
                    onClick={() => onRemoveExclusion?.(email)}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 bg-gray-25 text-sm text-Default hover:border-feedback-error transition-colors"
                  >
                    <span>{email}</span>
                    <span className="text-feedback-error font-medium">x</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="bg-gray-25 border border-gray-200 rounded px-4 py-3">
              <span className="text-sm text-gray-font">
                <span className="font-medium">Results:</span>{" "}
                {previewCount != null ? previewCount : "0"} potential recipients
              </span>
            </div>
          </div>
          
          <div className="flex justify-end">
              <LegacyButton
                text={isPreviewLoading ? "PREVIEWING..." : "PREVIEW RECIPIENT LIST"}
                style="filled"
                color="accent"
                content="text"
                size="small"
                className="!min-h-[42px]"
                onClick={handlePreviewClick}
                disabled={!canPreviewRecipientList}
              />
            </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-primary">
          {hasUpToDatePreview
            ? "*Please review some changes or updates made in the information to submit your data."
            : lastPreviewedSelection
              ? "*Preview the recipient list again before continuing to Step 3."
              : "*Use Preview recipient list to calculate matches, then continue to Step 3."}
        </span>
        <LegacyButton
          text="PROCEED TO STEP 3"
          icon={<ArrowRightIcon />}
          style="filled"
          color="accent"
          content="textIcon"
          size="small"
          onClick={() => onProceedToNextStep(currentSelection)}
          disabled={
            !hasTemplateSelected ||
            !hasValidConditions ||
            !hasUpToDatePreview ||
            isProceedLoading
          }
        />
      </div>
    </div>
  );
}
